/**
 * @typedef {Object} PrintState
 * @property {Function} initialize setup print state in DOM
 * @property {Function} terminate remove all DOM adjustments for print state 
 * 
 * @typedef {Object} ExceptionElements
 * @property {Node} elements each included element node
 * @property {Array<Node>} children all children of each element node
 * @property {Array<Node>} parents all children of each element node
 */

const CLASS_NAME_DISPLAY_EXCEPTION = '--print-display-exception--';
const CLASS_NAME_SPACING_EXCEPTION = '--print-spacing-exception--';

const CSS_SELECTOR_ROOT = ':root *';
const CSS_SELECTOR_WILDCARD = '*';
const CSS_SELECTOR_DELIMETER = ',';
const CSS_SELECTOR_DISPLAY_EXCEPTION = `:not(.${CLASS_NAME_DISPLAY_EXCEPTION})`;
const CSS_SELECTOR_SPACING_EXCEPTION = `:not(.${CLASS_NAME_SPACING_EXCEPTION})`;

const CSS_RULE_DISPLAY_NONE = 'display:none;';
const CSS_RULE_MARGIN_NONE = 'margin:0;';
const CSS_RULE_PADDING_NONE = 'padding:0;';
const CSS_RULE_VISIBILITY_VISIBLE = 'visibility:visible;';
const CSS_RULE_VISIBILITY_HIDDEN = 'visibility:hidden;';

/**
 * @param {string} str 
 * @returns {string}
 */
const trim = (str) => str.trim() || '';

/**
 * @param {string} selector 
 * @param {Array<string>|string} rules 
 * @returns {string}
 */
const cssRule = (selector, rules) => {
    const body = Array.isArray(rules) ? rules.join('') : rules;
    return selector ? `${selector}{${body}}` : '';
};

/**
 * @param {string} selector 
 * @returns {string}
 */
const appendDisplayException = (selector) => selector + CSS_SELECTOR_DISPLAY_EXCEPTION;

/**
 * @param {string} selector 
 * @returns {string}
 */
const appendSpacingException = (selector) => selector + CSS_SELECTOR_SPACING_EXCEPTION;

/**
 * @param {string} selector 
 * @returns {Array<string>}
 */
const createWildcardSelector = (selector) => {
    const lastChar = selector.substring(selector.length - 1);
    const wildcardSelector = lastChar !== CSS_SELECTOR_WILDCARD ? `${selector} ${CSS_SELECTOR_WILDCARD}` : null;

    return [
        selector,
        ...(wildcardSelector ? [ wildcardSelector ] : [])
    ];
};

/**
 * @param {Array<string>} selectors string of user entered selectors to generate variants for
 * @return {string}
 */
const createSelector = (selectors) => {
    return selectors
            .map(createWildcardSelector)
            .flat()
            .join(CSS_SELECTOR_DELIMETER);
};

/**
 * @param {string} selectors string of user entered selectors to be split
 * @returns {Array<string>}
 */
const splitSelectors = (selectors) => selectors?.split(CSS_SELECTOR_DELIMETER)?.map(trim) || [];

/**
 * @param {Array<string>} include css selector for elements to include
 * @returns {string}
 */
const includeElementsRule = (include) => {
    const selector = createSelector(include);
    const rule = cssRule(selector, CSS_RULE_VISIBILITY_VISIBLE);
    return rule;
};

/** 
 * @param {Array<string>} exclude css selector for elements to exclude
 * @return {string}
 */
const excludeElementsRule = (exclude) => {
    const selector = createSelector(exclude);
    const spacingSelector = selector ? appendSpacingException(selector) : '';
    const displaySelector = selector ? appendDisplayException(selector) : '';

    const visibilityRule = cssRule(selector, CSS_RULE_VISIBILITY_HIDDEN);
    const spacingRule = cssRule(spacingSelector, [ CSS_RULE_MARGIN_NONE, CSS_RULE_PADDING_NONE ]);
    const displayRule = cssRule(displaySelector, CSS_RULE_DISPLAY_NONE);

    return visibilityRule + spacingRule + displayRule;
};

/**
 * @param {Array<string>} include selector for all elements to include
 * @param {Array<string>} exclude selector for all element to exclude
 * @return {string} string representing a print stylesheet
 */
const createStyles = (include, exclude) => {
    let stylesheet = include.length ? excludeElementsRule([ CSS_SELECTOR_ROOT ]) : '';
    stylesheet += includeElementsRule(include);
    stylesheet += excludeElementsRule(exclude);
    return stylesheet;
}

/**
 * @param {string} styles string representing a print stylesheet
 * @returns {Object}
 */
const createStylesheet = (styles) => {
    let stylesheet = null;

    if(styles) {
        stylesheet = document.createElement('style');
        stylesheet.media = 'print';
        stylesheet.innerHTML = styles;
    }

    return stylesheet;
};

/**
 * @param {Node} element 
 * @returns {Array<Node>}
 */
const getAllParentElements = (element) => {
    const parents = [];

    const getParent = (child) => {
        const parent = child.parentElement;

        if(parent) {
            parents.push(parent);
            getParent(parent);
        }
    };

    getParent(element);

    return parents;
};

/**
 * @param {Array<string>} include array of CSS selector rules
 * @returns {ExceptionElements}
 */
const mapExceptionElements = (include) => {
    const exceptionElements = {
        elements: [],
        children: [],
        parents: []
    };

    include.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
            exceptionElements.elements.push(element);
            exceptionElements.children.push(...element.querySelectorAll(CSS_SELECTOR_WILDCARD));
            exceptionElements.parents.push(...getAllParentElements(element));
        });
    });

    return exceptionElements;
};

/**
 * 
 * @param {Node?} stylesheet node representing a style element
 * @param {ExceptionElements} exceptionElements array of nodes that need an exception from "display:none"
 * @returns {PrintState}
 */
const setupPrintState = (stylesheet, exceptionElements) => {
    const displayExceptions = Object.values(exceptionElements).flat();
    const spacingExceptions = [
        ...exceptionElements.elements,
        ...exceptionElements.children
    ];

    return {
        initialize() {
            if(stylesheet) {
                displayExceptions.forEach((element) => element.classList.add(CLASS_NAME_DISPLAY_EXCEPTION));
                spacingExceptions.forEach((element) => element.classList.add(CLASS_NAME_SPACING_EXCEPTION));
                document.body.appendChild(stylesheet);
            }
        },
        terminate() {
            if(stylesheet) {
                displayExceptions.forEach((element) => element.classList.remove(CLASS_NAME_DISPLAY_EXCEPTION));
                spacingExceptions.forEach((element) => element.classList.remove(CLASS_NAME_SPACING_EXCEPTION));
                document.body.removeChild(stylesheet);
            }
        }
    }
};

/**
 * @param {Array<string>} include selector for all elements to include
 * @param {Array<string>} exclude selector for all elements to exclude
 */
export default function print(include, exclude) {
    const styles = createStyles(include, exclude);
    const stylesheet = createStylesheet(styles);
    const exceptionElements = mapExceptionElements(include);
    const printState = setupPrintState(stylesheet, exceptionElements);

    printState.initialize();
    window.print();
    printState.terminate();
}

function onClickPrintHandler() {
    const include = this.dataset.include?.trim() || null;
    const exclude  = this.dataset.exclude?.trim() || null;
    print(
        splitSelectors(include),
        splitSelectors(exclude)
    );
};


$(() => {
    const rootEls = document.querySelectorAll('.js-print');
    rootEls.forEach((el) => {
        el.addEventListener('click', onClickPrintHandler);
    });
});
